import { useCallback } from 'react';
import { DateTime } from 'luxon';
import { useSessionStorage } from '~/modules/common/hooks';
import { useMeContext } from '~/modules/me/useMeContext';
import {
  dateToMidnightUTCString,
  dateToMidnightUTCObject
} from '~/modules/common/dates/convert';

export const useDateRangeState = () => {
  const { currentTimeInUserTimeZone } = useMeContext();
  const currentDate = dateToMidnightUTCString(currentTimeInUserTimeZone);
  const { storedValue: dateRange, setValue: setDateRange } = useSessionStorage(
    'smartBeatsDateRangeFilter',
    {
      startDate: dateToMidnightUTCObject(currentDate)
        .minus({ days: 6 })
        .toISO(),
      endDate: currentDate
    }
  );

  const onNext = useCallback(
    () =>
      setDateRange({
        startDate: dateToMidnightUTCString(
          dateToMidnightUTCObject(dateRange.startDate).plus({ days: 1 })
        ),
        endDate: dateToMidnightUTCString(
          dateToMidnightUTCObject(dateRange.endDate).plus({ days: 1 })
        )
      }),
    [dateRange, setDateRange]
  );

  const onPrevious = useCallback(
    () =>
      setDateRange({
        startDate: dateToMidnightUTCString(
          dateToMidnightUTCObject(dateRange.startDate).minus({ days: 1 })
        ),
        endDate: dateToMidnightUTCString(
          dateToMidnightUTCObject(dateRange.endDate).minus({ days: 1 })
        )
      }),
    [dateRange, setDateRange]
  );

  const onStartDateChange = useCallback(
    startDate => {
      const formattedStartDate = dateToMidnightUTCString(startDate);
      const startDateObject = dateToMidnightUTCObject(startDate);
      const endDateObject = dateToMidnightUTCObject(dateRange.endDate);

      setDateRange({
        startDate: formattedStartDate,
        endDate: dateToMidnightUTCString(
          DateTime.max(startDateObject, endDateObject)
        )
      });
    },
    [dateRange, setDateRange]
  );

  const onEndDateChange = useCallback(
    endDate => {
      const formattedEndDate = dateToMidnightUTCString(endDate);
      const startDateObject = dateToMidnightUTCObject(dateRange.startDate);
      const endDateObject = dateToMidnightUTCObject(endDate);

      setDateRange({
        startDate: dateToMidnightUTCString(
          DateTime.min(startDateObject, endDateObject)
        ),
        endDate: formattedEndDate
      });
    },
    [dateRange, setDateRange]
  );

  return {
    dateRange,
    onStartDateChange,
    onEndDateChange,
    onNext,
    onPrevious
  };
};

export default useDateRangeState;
